import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  HiShoppingCart,
  HiSearch,
  HiArrowLeft,
  HiX,
  HiPlus,
  HiMinus,
  HiTrash,
  HiPhone,
  HiCreditCard,
  HiCash,
  HiClock,
} from "react-icons/hi";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PaymentModal from "../components/PaymentModal";
import { getLoggedInUser } from "../utils/authUser";

const API_BASE = "https://app.caaficare.so/api";

const getPrice = (p) => p?.price ?? p?.unit_price ?? p?.amount ?? 0;
const getEffectivePrice = (p) => {
  const discount = p?.discount_price ?? p?.discountPrice;
  if (discount && parseFloat(discount) > 0) return parseFloat(discount);
  return parseFloat(getPrice(p)) || 0;
};
const getImage = (p) =>
  p?.image ||
  p?.photo ||
  "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?q=80&w=400&auto=format&fit=crop";

/** Product is visible when is_active is true; inactive products are hidden from ordering */
const isProductActive = (p) => p?.is_active !== false;

/** Check if pharmacy is currently open based on openingTime/closingTime (HH:mm format) */
const isPharmacyOpen = (pharmacy) => {
  if (!pharmacy) return true;
  const open = pharmacy?.openingTime || pharmacy?.opening_time;
  const close = pharmacy?.closingTime || pharmacy?.closing_time;
  if (!open || !close) return true;

  const now = new Date();
  const [oh, om] = (open + "").split(":").map(Number);
  const [ch, cm] = (close + "").split(":").map(Number);
  const nowMinutes = now.getHours() * 60 + now.getMinutes();
  const openMinutes = (oh || 0) * 60 + (om || 0);
  const closeMinutes = (ch || 0) * 60 + (cm || 0);

  if (openMinutes <= closeMinutes) {
    return nowMinutes >= openMinutes && nowMinutes < closeMinutes;
  }
  return nowMinutes >= openMinutes || nowMinutes < closeMinutes;
};

const formatTime = (t) => {
  if (!t) return "";
  const [h, m] = (t + "").split(":").map(Number);
  if (h === undefined || isNaN(h)) return t;
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m || 0).padStart(2, "0")} ${ampm}`;
};

const PharmacyProducts = () => {
  const { pharmacyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const pharmacy = location.state?.pharmacy || null;

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [showCart, setShowCart] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  const [placingOrder, setPlacingOrder] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pharmacyInfo, setPharmacyInfo] = useState(pharmacy);

  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [orderType, setOrderType] = useState("Pickup");
  const [districts, setDistricts] = useState([]);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [pickupPointsLoading, setPickupPointsLoading] = useState(false);
  const [fromDistrict, setFromDistrict] = useState("");
  const [toDistrict, setToDistrict] = useState("");
  const [pickupPointId, setPickupPointId] = useState("");

  const categories = useMemo(() => {
    const unique = new Set(
      products.map((p) => (p.category || "").trim()).filter(Boolean)
    );
    return ["All", ...Array.from(unique)];
  }, [products]);

  const loadProducts = useCallback(async () => {
    if (!pharmacyId) {
      setProducts([]);
      setLoading(false);
      return;
    }
    try {
      const [productsRes, pharmacyRes] = await Promise.all([
        axios.get(
          `${API_BASE}/product?pharmacy_id=${encodeURIComponent(pharmacyId)}&page=1&pageSize=500`
        ),
        axios.get(`${API_BASE}/pharmacy/${pharmacyId}`).catch(() => ({ data: null })),
      ]);

      const body = productsRes.data;
      const raw = Array.isArray(body)
        ? body
        : Array.isArray(body?.data)
        ? body.data
        : [];
      const list = raw.filter((p) => isProductActive(p));
      setProducts(list);

      const fetchedPharmacy = pharmacyRes?.data?.success ? pharmacyRes.data.data : null;
      setPharmacyInfo((prev) => {
        const merged = fetchedPharmacy ? { ...(prev || {}), ...fetchedPharmacy } : prev;
        if (!merged && list.length > 0 && list[0]?.pharmacy) {
          return list[0].pharmacy;
        }
        return merged || prev;
      });
    } catch (error) {
      console.error("Products load error:", error);
      toast.error("Failed to load products");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [pharmacyId]);

  useEffect(() => {
    if (pharmacy) setPharmacyInfo(pharmacy);
  }, [pharmacy]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  useEffect(() => {
    let filtered = products.filter((p) => isProductActive(p));
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((p) => {
        const name = (p.item_name || p.name || "").toLowerCase();
        const desc = (p.description || "").toLowerCase();
        const cat = (p.category || "").toLowerCase();
        return name.includes(q) || desc.includes(q) || cat.includes(q);
      });
    }
    if (selectedCategory !== "All") {
      filtered = filtered.filter((p) => (p.category || "").trim() === selectedCategory);
    }
    setFilteredProducts(filtered);
  }, [products, searchQuery, selectedCategory]);

  const addToCart = (product) => {
    if (!isPharmacyOpen(pharmacyInfo)) {
      toast.error("This pharmacy is currently closed. Orders are not available.");
      return;
    }
    const existing = cart.find((c) => c.id === product.id);
    const stock = product.stock ?? Number.MAX_SAFE_INTEGER;
    if (existing) {
      if (existing.quantity < stock) {
        setCart(
          cart.map((c) =>
            c.id === product.id ? { ...c, quantity: c.quantity + 1 } : c
          )
        );
      } else {
        toast.warning("Maximum available quantity reached");
      }
    } else {
      if (stock > 0 || stock === undefined) {
        setCart([...cart, { ...product, quantity: 1 }]);
      } else {
        toast.warning("Out of stock");
      }
    }
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter((c) => c.id !== productId));
  };

  const updateQuantity = (productId, delta) => {
    const item = cart.find((c) => c.id === productId);
    if (!item) return;
    const product = products.find((p) => p.id === productId);
    const stock = product?.stock ?? Number.MAX_SAFE_INTEGER;
    const newQty = item.quantity + delta;
    if (newQty <= 0) removeFromCart(productId);
    else if (newQty <= stock) {
      setCart(
        cart.map((c) =>
          c.id === productId ? { ...c, quantity: newQty } : c
        )
      );
    } else {
      toast.warning("Maximum available quantity reached");
    }
  };

  const getTotalPrice = () =>
    cart.reduce((sum, item) => sum + getEffectivePrice(item) * item.quantity, 0);

  const getTotalItems = () => cart.reduce((sum, item) => sum + item.quantity, 0);

  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const res = await axios.get(`${API_BASE}/districts?page=1&pageSize=500`);
        const data = res.data?.data || res.data || [];
        setDistricts(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error("Districts fetch error:", e);
      }
    };
    fetchDistricts();
  }, []);

  // When order is Delivery, lock "From District" to the pharmacy's district (user cannot change it)
  useEffect(() => {
    if (orderType !== "Delivery" || !pharmacyInfo?.district || districts.length === 0) return;
    const pharmacyDistrictName = String(pharmacyInfo.district).trim();
    if (!pharmacyDistrictName) return;
    const match = districts.find(
      (d) => d.name && String(d.name).trim().toLowerCase() === pharmacyDistrictName.toLowerCase()
    );
    if (match?.code) {
      setFromDistrict(match.code);
    }
  }, [orderType, pharmacyInfo?.district, districts]);

  useEffect(() => {
    if (orderType === "Delivery" && fromDistrict && toDistrict && fromDistrict !== toDistrict) {
      setPickupPointsLoading(true);
      const fetchPickupPoints = async () => {
        try {
          const res = await axios.get(`${API_BASE}/pickupPoints?page=1&pageSize=500`);
          const all = res.data?.data || res.data || [];
          const list = Array.isArray(all) ? all : [];
          const filtered = list.filter(
            (pp) => pp.from === fromDistrict && pp.to === toDistrict
          );
          setPickupPoints(filtered);
          setPickupPointId("");
        } catch (e) {
          console.error("Pickup points fetch error:", e);
          setPickupPoints([]);
        } finally {
          setPickupPointsLoading(false);
        }
      };
      fetchPickupPoints();
    } else {
      setPickupPoints([]);
      setPickupPointId("");
      setPickupPointsLoading(false);
    }
  }, [orderType, fromDistrict, toDistrict]);

  const loadCustomerFromStorage = useCallback(() => {
    try {
      const data = getLoggedInUser();
      if (!data) return;
      if (data.full_name || data.fullName || data.name) {
        setCustomerName(data.full_name || data.fullName || data.name);
      }
      if (data.phone || data.tel) {
        setCustomerPhone(data.phone || data.tel || "");
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (showCheckout) loadCustomerFromStorage();
  }, [showCheckout, loadCustomerFromStorage]);

  const getDistrictName = (code) => {
    const d = districts.find((x) => x.code === code);
    return d ? d.name : code;
  };

  // Step 1: Validate and open payment modal (order not created yet)
  const proceedToPayment = () => {
    if (!isPharmacyOpen(pharmacyInfo)) {
      toast.error("This pharmacy is currently closed. Orders are not available.");
      return;
    }
    if (!customerName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!customerPhone.trim()) {
      toast.error("Please enter your phone number");
      return;
    }
    if (orderType === "Delivery") {
      if (!fromDistrict || !toDistrict) {
        toast.error("Please select from and to district");
        return;
      }
      if (!pickupPointId) {
        toast.error("Please select a pickup point");
        return;
      }
    }
    setShowCheckout(false);
    setShowPayment(true);
  };

  // Create order ONLY when user confirms payment (clicks "I've Paid" in PaymentModal)
  const createOrderOnPaymentConfirm = async () => {
    const deliveryFee =
      orderType === "Delivery" && pickupPoints.find((p) => String(p.id) === pickupPointId)
        ? parseFloat(
            pickupPoints.find((p) => String(p.id) === pickupPointId)?.price || 0
          )
        : 0;
    const subtotal = getTotalPrice();
    const totalAmount = (subtotal + deliveryFee).toFixed(2);

    const items = cart.map((item) => ({
      product_id: parseInt(item.id || item._id),
      quantity: parseInt(item.quantity) || 1,
    }));

    const payload = {
      customerName: customerName.trim(),
      customerPhone: customerPhone.trim(),
      items,
      type: orderType,
      from_district: orderType === "Delivery" ? fromDistrict : null,
      to_district: orderType === "Delivery" ? toDistrict : null,
      pickup_point_id: orderType === "Delivery" ? pickupPointId : null,
    };

    setPlacingOrder(true);
    try {
      const res = await axios.post(`${API_BASE}/order`, payload);
      if (res.data?.success) {
        const orderNumber = res.data?.data?.order_number || "";
        setCart([]);
        setCustomerName("");
        setCustomerPhone("");
        setOrderType("Pickup");
        setFromDistrict("");
        setToDistrict("");
        setPickupPointId("");
        setLastOrder({ orderNumber, totalAmount });
        toast.success("Order placed successfully!");
        return true;
      } else {
        toast.error(res.data?.error || "Failed to place order");
        return false;
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.error ||
          error?.message ||
          "Failed to place order"
      );
      return false;
    } finally {
      setPlacingOrder(false);
    }
  };

  // Total amount for payment modal (before or after order)
  const getPaymentTotalAmount = () => {
    if (lastOrder?.totalAmount) return lastOrder.totalAmount;
    const deliveryFee =
      orderType === "Delivery" && pickupPoints.find((p) => String(p.id) === pickupPointId)
        ? parseFloat(
            pickupPoints.find((p) => String(p.id) === pickupPointId)?.price || 0
          )
        : 0;
    return (getTotalPrice() + deliveryFee).toFixed(2);
  };

  // Use merchant_number from payment method as-is, add amount after last *
  // e.g. *712*622222154* -> *712*622222154*4.00#
  const handlePaymentComplete = async () => {
    const success = await createOrderOnPaymentConfirm();
    return success;
  };

  const handleCheckout = () => {
    setShowCart(false);
    setShowCheckout(true);
  };


  if (!pharmacyId && !pharmacy) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500 mb-4">Pharmacy not found.</p>
          <button
            onClick={() => navigate("/pharmacies")}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl font-bold"
          >
            Browse Pharmacies
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        onAppointmentClick={() => navigate("/hospitals")}
        onNurseAppointmentClick={() => navigate("/nurses-list")}
        onVideoConsultingClick={() => navigate("/doctors")}
        onApplyNurseClick={() => {}}
        onApplyDoctorClick={() => {}}
      />

      <div className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          {pharmacyInfo && !isPharmacyOpen(pharmacyInfo) && (pharmacyInfo.openingTime || pharmacyInfo.opening_time) && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-center gap-3">
              <HiClock className="w-8 h-8 text-red-500 flex-shrink-0" />
              <div>
                <p className="font-bold text-red-800">This pharmacy is currently closed</p>
                <p className="text-sm text-red-700">
                  Hours: {formatTime(pharmacyInfo.openingTime || pharmacyInfo.opening_time)} –{" "}
                  {formatTime(pharmacyInfo.closingTime || pharmacyInfo.closing_time)}. Orders are not available.
                </p>
              </div>
            </div>
          )}

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <button
                onClick={() => navigate("/pharmacies")}
                className="flex items-center gap-2 text-gray-600 hover:text-blue-600 mb-2 transition-colors"
              >
                <HiArrowLeft className="w-5 h-5" />
                <span className="text-sm font-bold">Back to Pharmacies</span>
              </button>
              <h1 className="text-3xl font-black text-gray-900">
                {pharmacyInfo?.name || pharmacy?.name || "Products"}
              </h1>
              <p className="text-gray-500">Browse and order products</p>
              {pharmacyInfo && (pharmacyInfo.openingTime || pharmacyInfo.opening_time) && (
                <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                  <HiClock className="w-4 h-4" />
                  {formatTime(pharmacyInfo.openingTime || pharmacyInfo.opening_time)} –{" "}
                  {formatTime(pharmacyInfo.closingTime || pharmacyInfo.closing_time)}
                  {!isPharmacyOpen(pharmacyInfo) && (
                    <span className="ml-2 font-bold text-red-600">(Closed)</span>
                  )}
                </p>
              )}
            </div>

            <button
              onClick={() => {
                if (!isPharmacyOpen(pharmacyInfo)) {
                  toast.error("This pharmacy is currently closed. Orders are not available.");
                  return;
                }
                setShowCart(true);
              }}
              disabled={cart.length === 0 || !isPharmacyOpen(pharmacyInfo)}
              className="relative inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              <HiShoppingCart className="w-6 h-6" />
              Cart ({getTotalItems()})
              {getTotalItems() > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs flex items-center justify-center">
                  {getTotalItems()}
                </span>
              )}
            </button>
          </div>

          {/* Search & Categories */}
          <div className="mb-8 flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <HiSearch className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 outline-none"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-4 py-2 rounded-xl font-bold text-sm transition-all ${
                    selectedCategory === cat
                      ? "bg-blue-600 text-white"
                      : "bg-white border-2 border-gray-200 hover:border-blue-300"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center min-h-[40vh]">
              <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center">
              <HiShoppingCart className="w-24 h-24 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900">No products found</h3>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  className="bg-white rounded-2xl shadow-lg border-2 border-gray-100 p-4 hover:shadow-xl transition-all"
                >
                  <div className="aspect-square rounded-xl overflow-hidden bg-gray-100 mb-4">
                    <img
                      src={getImage(product)}
                      alt={product.item_name || "Product"}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1 line-clamp-2">
                    {product.item_name || product.name || "Product"}
                  </h3>
                  {product.category && (
                    <p className="text-xs text-blue-600 font-semibold mb-1">
                      {product.category}
                    </p>
                  )}
                  {(product.description || product.desc) && (
                    <p className="text-xs text-gray-500 mb-2 line-clamp-2">
                      {product.description || product.desc}
                    </p>
                  )}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      {(product.discount_price || product.discountPrice) && parseFloat(product.discount_price || product.discountPrice) > 0 ? (
                        <>
                          <span className="text-sm text-gray-400 line-through">
                            ${Number(getPrice(product)).toFixed(2)}
                          </span>
                          <span className="text-lg font-black text-green-600">
                            ${Number(getEffectivePrice(product)).toFixed(2)}
                          </span>
                        </>
                      ) : (
                        <span className="text-lg font-black text-green-600">
                          ${Number(getPrice(product)).toFixed(2)}
                        </span>
                      )}
                    </div>
                    {product.stock != null && (
                      <span className="text-xs text-gray-500">
                        {product.stock > 0 ? `${product.stock} in stock` : "Out of stock"}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => addToCart(product)}
                    disabled={product.stock === 0 || !isPharmacyOpen(pharmacyInfo)}
                    className="w-full py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {!isPharmacyOpen(pharmacyInfo)
                      ? "Pharmacy Closed"
                      : product.stock === 0
                      ? "Out of Stock"
                      : "Add to Cart"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Cart Modal */}
      {showCart && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-black">Shopping Cart</h2>
              <button onClick={() => setShowCart(false)} className="p-2">
                <HiX className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {cart.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Your cart is empty</p>
              ) : (
                <div className="space-y-4">
                  {cart.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl"
                    >
                      <img
                        src={getImage(item)}
                        alt=""
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-bold text-gray-900 truncate">
                          {item.item_name || item.name || "Product"}
                        </p>
                        <p className="text-sm text-gray-500">
                          ${Number(getEffectivePrice(item)).toFixed(2)} each
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => updateQuantity(item.id, -1)}
                          className="p-2 rounded-lg bg-gray-200 hover:bg-gray-300"
                        >
                          <HiMinus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-bold">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, 1)}
                          className="p-2 rounded-lg bg-gray-200 hover:bg-gray-300"
                        >
                          <HiPlus className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="font-bold text-green-600 w-20 text-right">
                        ${(getEffectivePrice(item) * item.quantity).toFixed(2)}
                      </p>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                      >
                        <HiTrash className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {cart.length > 0 && (
              <div className="p-6 border-t">
                {!isPharmacyOpen(pharmacyInfo) && (
                  <p className="text-sm text-red-600 font-bold mb-3">
                    Pharmacy is closed — checkout unavailable
                  </p>
                )}
                <p className="text-xl font-black text-gray-900 mb-4">
                  Total: ${getTotalPrice().toFixed(2)}
                </p>
                <button
                  onClick={() => {
                    if (!isPharmacyOpen(pharmacyInfo)) {
                      toast.error("This pharmacy is currently closed. Orders are not available.");
                      return;
                    }
                    handleCheckout();
                  }}
                  disabled={!isPharmacyOpen(pharmacyInfo)}
                  className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Proceed to Checkout
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {showCheckout && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 overflow-y-auto">
          <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-black">Order Details</h2>
              <button
                onClick={() => !placingOrder && setShowCheckout(false)}
                disabled={placingOrder}
                className="p-2"
              >
                <HiX className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Enter your name"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(e.target.value)}
                  placeholder="Enter phone number"
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-blue-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Order Type *
                </label>
                <div className="flex gap-4">
                  <button
                    onClick={() => setOrderType("Pickup")}
                    className={`flex-1 py-3 rounded-xl font-bold border-2 ${
                      orderType === "Pickup"
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-200"
                    }`}
                  >
                    Pickup
                  </button>
                  <button
                    onClick={() => setOrderType("Delivery")}
                    className={`flex-1 py-3 rounded-xl font-bold border-2 ${
                      orderType === "Delivery"
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-200"
                    }`}
                  >
                    Delivery
                  </button>
                </div>
              </div>

              {orderType === "Delivery" && (
                <>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      From District (Pharmacy)
                    </label>
                    <div className="rounded-xl border-2 border-gray-200 bg-gray-100 px-4 py-3 text-gray-700 font-semibold cursor-not-allowed select-none">
                      {pharmacyInfo?.district
                        ? (districts.find((d) => d.code === fromDistrict)?.name || pharmacyInfo.district)
                        : "—"}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Delivery starts from the pharmacy. This cannot be changed.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      To District *
                    </label>
                    <div className="max-h-40 overflow-y-auto pr-2 space-y-2 rounded-xl border-2 border-gray-200 bg-gray-50/50 p-2">
                      {districts
                        .filter((d) => d.code !== fromDistrict)
                        .map((d) => (
                          <button
                            key={d.id || d.code}
                            onClick={() => setToDistrict(d.code)}
                            className={`w-full text-left px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                              toDistrict === d.code
                                ? "bg-blue-600 text-white border-2 border-blue-600"
                                : "bg-white border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50/30"
                            }`}
                          >
                            {d.name}
                          </button>
                        ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-bold text-gray-700 mb-2">
                      Pickup Point * {pickupPoints.length > 0 && `(${pickupPoints.length} available)`}
                    </label>
                    {pickupPointsLoading ? (
                      <div className="rounded-xl border-2 border-gray-200 bg-gray-50 px-4 py-6 text-center text-gray-500 text-sm">
                        Loading pickup points...
                      </div>
                    ) : pickupPoints.length === 0 ? (
                      <div className="rounded-xl border-2 border-gray-200 bg-gray-50 px-4 py-6 text-center text-gray-500 text-sm">
                        {toDistrict ? "No pickup points available for this route" : "Select a To District first"}
                      </div>
                    ) : (
                      <div className="max-h-40 overflow-y-auto pr-2 space-y-2 rounded-xl border-2 border-gray-200 bg-gray-50/50 p-2">
                        {pickupPoints.map((pp) => (
                          <button
                            key={pp.id}
                            type="button"
                            onClick={() => setPickupPointId(String(pp.id))}
                            className={`w-full text-left px-4 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-between gap-3 ${
                              pickupPointId === String(pp.id)
                                ? "bg-blue-600 text-white border-2 border-blue-600"
                                : "bg-white border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50/30"
                            }`}
                          >
                            <span className="truncate">
                              {getDistrictName(pp.from)} → {getDistrictName(pp.to)}
                            </span>
                            <span className={`flex-shrink-0 font-bold ${pickupPointId === String(pp.id) ? "text-white" : "text-green-600"}`}>
                              ${pp.price}
                            </span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className="bg-gray-50 rounded-xl p-4">
                <p className="font-bold text-gray-900 mb-3">Order Summary</p>
                {cart.map((item) => (
                  <div
                    key={item.id}
                    className="flex justify-between text-sm mb-2"
                  >
                    <span>
                      {item.item_name || item.name} × {item.quantity}
                    </span>
                    <span className="font-bold">
                      ${(getEffectivePrice(item) * item.quantity).toFixed(2)}
                    </span>
                  </div>
                ))}
                {orderType === "Delivery" &&
                  pickupPoints.find((p) => String(p.id) === pickupPointId) && (
                    <div className="flex justify-between text-sm mb-2">
                      <span>Delivery Fee</span>
                      <span className="font-bold">
                        $
                        {
                          pickupPoints.find((p) => String(p.id) === pickupPointId)
                            ?.price
                        }
                      </span>
                    </div>
                  )}
                <div className="flex justify-between pt-2 border-t font-black text-lg">
                  <span>Total</span>
                  <span className="text-green-600">
                    $
                    {(
                      getTotalPrice() +
                      (orderType === "Delivery" &&
                      pickupPoints.find((p) => String(p.id) === pickupPointId)
                        ? parseFloat(
                            pickupPoints.find((p) => String(p.id) === pickupPointId)
                              ?.price || 0
                          )
                        : 0)
                    ).toFixed(2)}
                  </span>
                </div>
              </div>

                <button
                  type="button"
                  onClick={proceedToPayment}
                  className="w-full py-4 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700"
                >
                  Proceed to Payment
                </button>
            </div>
          </div>
        </div>
      )}

      {/* PaymentModal: Order is created ONLY when user clicks "I've Paid" (payment confirmation) */}
      <PaymentModal
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        appointmentPrice={getPaymentTotalAmount()}
        onPaymentComplete={handlePaymentComplete}
      />

      {/* Success modal after order is placed */}
      {lastOrder && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-2xl shadow-2xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <HiShoppingCart className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-black text-gray-900 mb-2">Order Placed Successfully</h3>
            <p className="text-lg font-bold text-gray-700">Order #{lastOrder.orderNumber}</p>
            <p className="text-sm text-gray-500 mt-1">Total: ${lastOrder.totalAmount}</p>
            <button
              type="button"
              onClick={() => setLastOrder(null)}
              className="mt-6 w-full py-4 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        </div>
      )}

 
    </div>
  );
};

export default PharmacyProducts;
